import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatScheduleRange } from '../../../utils/formatSchedule'
import Loading from '../../ui/Loading'
import ErrorMessage from '../../ui/ErrorMessage'
import { formatTime } from '../../../utils/formatDateAndTime'
import { useQueryData } from '../../../hooks/api/useQueryData'
import { formatCurrency } from '../../../utils/formatAmount'
import ClientPackagesHeader from '../../common/headers/ClientPackagesHeader'
import ViewClientAvailPackageProduct from '../Admin/ViewClientAvailPackageProduct'
import { selectCurrentClientId } from '../../../features/client/clientSlice'
import { useDynamicQueryData } from '../../../hooks/api/useDynamicQueryData'

export default function ClientPackages() {

    const clientId = useSelector(selectCurrentClientId);
    const { clientInfo } = useQueryData()
    const [openPackageId, setOpenPackageId] = useState<number | null>(null);
    const [isShowProducts, setShowProducts] = useState<boolean>(false);
    // const [packageProducts, setPackageProducts] = useState<ProductSummary[] | null>(null)
    const { packageSummaryBaseOnIdQuery, isErrorPackageSummaryBaseOnIdQuery,isLoadingPackageSummaryBaseOnIdQuery, errorMessagePackageSummaryBaseOnIdQuery } = useDynamicQueryData({clientId})

    
    const onCloseProductsModal = () => {
        setShowProducts(false)
        // setPackageProducts(null)
        setOpenPackageId(null)
    }
    
    const selectedPackage = useMemo(() => {
        return packageSummaryBaseOnIdQuery?.find(
            pkg => pkg.clientPackageId === openPackageId
        );
    }, [packageSummaryBaseOnIdQuery, openPackageId]);

    return (
        <div className="fixed inset-0 m-0 p-0 bg-black bg-opacity-30 backdrop-blur-sm flex justify-center items-center z-[102] overflow-hidden">
            <div className="relative w-[90%] mx-5 bg-white rounded-lg shadow px-5 flex md:w-[75%] lg:max-w-[55%] lg:mx-auto overflow-hidden">
                <div className="flex flex-col w-full min-h-[500px] max-h-[600px] overflow-hidden">
                    <div className="flex flex-col py-5 h-full">
                        {/* Header */}
                        <ClientPackagesHeader/>
                        <hr className="h-px bg-gray-200 border-0" />
                        <div className='overflow-hidden rounded mt-2'>
                            <div className='px-2 h-full rounded overflow-y-auto'>
                                { isLoadingPackageSummaryBaseOnIdQuery ? (
                                    <div className="flex justify-center items-center h-full">
                                        <Loading message='Loading'/>
                                    </div>
                                ) : isErrorPackageSummaryBaseOnIdQuery ? (
                                    <div className="flex justify-center items-center h-full">
                                        <ErrorMessage message={errorMessagePackageSummaryBaseOnIdQuery}/>
                                    </div>
                                ) : clientInfo?.packageSummary.length === 0 ? (
                                    <div className="flex justify-center items-center h-64">
                                        <p className="text-gray-400 font-medium text-center">
                                            No package selected
                                        </p>
                                    </div>
                                ) : (
                                <div className='pt-2 space-y-3 overflow-x-hidden'>
                                    <div className='grid grid-cols-1 space-y-2 '>
                                        {packageSummaryBaseOnIdQuery?.map((pkg) => {
                                            const cups = pkg.qty;
                                            const specialTotalValue = pkg.totalValue;
                                            const isCupBased = pkg.packageType === 'Cup-Based'
                                            const isAmountBased = pkg.packageType === 'Amount-Based'

                                            return(
                                                <div key={pkg.clientPackageId} className='flex flex-col'>
                                                    <div className={`border-l-8 border-gray-400 rounded border p-2.5`}>
                                                        <div className={`rounded-r rounded-br grid grid-cols-3 gap-2 `}>
                                                            <div className='flex flex-col'>
                                                                <p className='text-sm'>Package Name:</p>
                                                                <p className='text-sm font-semibold'>{pkg.packageName}</p>
                                                            </div>
                                                            <div className='flex flex-col'>
                                                                <p className='text-sm'>Expiration Date:</p>
                                                                <p className='text-sm font-semibold'>
                                                                    {
                                                                        pkg?.validFrom && pkg?.validTo ? formatScheduleRange(pkg?.validFrom, pkg?.validTo, 'utc') : ''
                                                                    }
                                                                </p>
                                                            </div>
                                                            <div className='flex flex-col'>
                                                                <p className='text-sm'>Expiration Time:</p>
                                                                <div className='text-sm font-semibold flex'>
                                                                    {pkg?.validFrom && pkg?.validTo
                                                                        ? `${formatTime(pkg.validFrom)} - ${formatTime(pkg.validTo)}`
                                                                        : ''
                                                                    }
                                                                </div>
                                                            </div>
                                                            {isCupBased && (
                                                                <div className='flex flex-col'>
                                                                    <p className='text-sm'>Cups:</p>
                                                                    <p className='text-sm font-semibold'>{cups}</p>
                                                                </div>
                                                            )} 
                                                            {isCupBased && (
                                                                <div className='flex flex-col'>
                                                                    <p className='text-sm'>Available Cups:</p>
                                                                    <p className='text-sm font-semibold'>{pkg.remainingQty}</p>
                                                                </div>
                                                            )}
                                                            {isAmountBased && (
                                                                <div className='flex flex-col'>
                                                                    <p className='text-sm'>Total Value:</p>
                                                                    <p className='text-sm font-semibold'>{formatCurrency(specialTotalValue)}</p>
                                                                </div>
                                                            )}
                                                            {isAmountBased && (
                                                                <div className='flex flex-col'>
                                                                    <p className='text-sm'>Amount Spent:</p>
                                                                    <p className='text-sm font-semibold'>{formatCurrency(pkg.spentAmount ?? 0)}</p>
                                                                </div>
                                                            )}
                                                            {isAmountBased && (
                                                                <div className='flex flex-col'>
                                                                    <p className='text-sm'>Reaming Balance:</p>
                                                                    <p className='text-sm font-semibold'>{formatCurrency(pkg.remainingBalance ?? 0)}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div onClick={() => {
                                                            // togglePackage(pkg.clientPackageId)
                                                            // setPackageProducts(pkg.products)
                                                            setShowProducts(true)
                                                            setOpenPackageId(pkg.clientPackageId)
                                                        }} className="cursor-pointer">
                                                            <p className="text-xs cursor-pointer text-center font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                                                                View Products
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                                )}
                            </div>
                            {isShowProducts && (
                                <ViewClientAvailPackageProduct
                                    isLoading={isLoadingPackageSummaryBaseOnIdQuery}
                                    isError={isErrorPackageSummaryBaseOnIdQuery}
                                    errormessage={errorMessagePackageSummaryBaseOnIdQuery}
                                    product={selectedPackage?.products ?? []}
                                    onClose={onCloseProductsModal}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
